<template>
  <div class="homepage-wrapper">
    <div class="container">
      <div class="home-page-container">
        <div class="card w-100">
          <section class="hero">
            <div class="hero-title-container">
              <p class="hero-title fade-in">Apertura</p>
              <p class="hero-subtitle fade-in">Where science becomes <i class="highlighted-text">art</i></p>
              <p class="hero-paragraph fade-in">Some of the most extraordinary images ever created are hidden inside
                laboratories, simulations, and scientific research.
                <strong>Apertura brings them into the light.</strong></p>
              <div class="button-container fade-in">
                <button class="button-primary" @click="navigateToDiscover">Discover</button>
                <button class="button-secondary">
                  <span>Upload your own</span>
                </button>
              </div>
            </div>
          </section>
          <section class="options-container">
            <div class="options-title">
              Let the images speak for themselves.
            </div>
            <div class="options-card-container">
              <div class="card option-card">
                <p class="option-card-title">Discover</p>
                <p class="option-card-paragraph">
                  Explore scientific imagery from researchers, students, and enthusiasts around the world.
                </p>
              </div>
              <div class="card option-card">
                <p class="option-card-title">Share</p>
                <p class="option-card-paragraph">
                  Upload your own images and showcase the beauty hidden within your research.
                </p>
              </div>
              <div class="card option-card">
                <p class="option-card-title">Compete</p>
                <p class="option-card-paragraph">
                  Participate in community-driven contests and gain recognition for your work.
                </p>
              </div>
            </div>
          </section>
          <div class="gallery-title-container">
            <p class="gallery-title">
              A taste of what awaits
            </p>
          </div>
          <section class="gallery">
            <article v-for="image in images" :key="image.id" class="image-card">
              <img :src="image.url" :alt="image.name" loading="lazy">
              <div class="overlay">
                <h2>{{ image.name }}</h2>
                <span>❤ {{ image.like_count }}</span>
              </div>
            </article>
          </section>
          <div class="see-more-container">
            <button class="see-more-button button-secondary" @click="navigateToDiscover">
              <span>See more →</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, ref} from "vue";
import apiService from "@/services/apiService";
import router from "@/router";

const images = ref([]);

onMounted(async () => {
  await loadFeed()
})

async function loadFeed() {
  images.value = await apiService.getRandomizedFeed(10)
}

async function navigateToDiscover() {
  await router.push("/discover");
}

</script>

<style scoped>
.homepage-wrapper {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
  height: 100%;
}

.home-page-container {
  display: flex;
  height: 100%;
  width: 100%;
  align-items: start;
  justify-content: start;
}

.hero-title-container {
  width: 100%;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
}

.fade-in {
  opacity: 0;
  transform: translateY(10px);
  animation: slideUp 1s ease forwards;
}

.hero-title {
  color: black;
  font-size: 8rem;
  margin: 0;
  animation-delay: 0.4s;
}

.hero-subtitle {
  font-size: 3rem;
  margin: 0;
  animation-delay: 0.8s;
}

.button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  animation-delay: 1.5s;
}

.button-primary {
  background-color: black;
  border-radius: 0;
  padding: 0.8rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.button-secondary::before {
  content: "";
  position: absolute;

  left: 0;
  bottom: 0;

  width: 100%;
  height: 0;

  background: black;
  z-index: 0;

  transition: height 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.button-secondary:hover::before {
  height: 100%;
}

.button-secondary span {
  position: relative;
  z-index: 1;
}

.button-secondary:hover {
  color: white;
}

.hero-paragraph {
  animation-delay: 1s;
  width: 60%;
  margin-top: 4rem;
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.highlighted-text {
  font-style: italic;
  padding-right: 1rem;
}

.options-container {
  background-color: whitesmoke;
  height: 55vh;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
}

.options-card-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 3rem;
}

.option-card {
  width: 25%;
  padding: 5rem 3rem;
  min-height: 20rem;
}

.options-title {
  font-size: 2rem;
  font-weight: bolder;
  margin-bottom: 2rem;
}

.option-card-title {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
}

.option-card-paragraph {
  text-align: center;
  color: #3a3a3a;
}

.gallery-title-container {
  width: 100%;
}

.gallery-title {
  font-size: 2rem;
  margin-top: 5rem;
}

.gallery {
  columns: 4 300px;
  column-gap: 1rem;
}

.image-card {
  position: relative;
  margin-bottom: 1rem;
  break-inside: avoid;
  overflow: hidden;
  border-radius: 18px;
  background: #111;
  cursor: pointer;
}

.image-card img {
  width: 100%;
  display: block;
  transition: transform 0.3s ease;
}

.image-card:hover img {
  transform: scale(1.03);
}

.overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem;
  background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.85),
      rgba(0, 0, 0, 0)
  );
  color: white;
  opacity: 0;
  transition: opacity 0.25s ease;
  display: flex;
  justify-content: space-between;
  align-items: end;
}

.image-card:hover .overlay {
  opacity: 1;
}

.see-more-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 5rem;
}

.see-more-button {
  width: 20rem;
}
</style>